import type { Scenario } from '@orgsim/shared';
import { create } from 'zustand';

export type Screen = 'start' | 'editor' | 'run' | 'users';

// The editor's working state, kept outside the component: an expired session swaps the
// editor for the login screen, and the draft must be there again after signing back in.
export type EditorDraft = {
  scenario: Scenario;
  published: boolean;
  savedSnapshot: string;
  savedPublished: boolean;
};

type AppStore = {
  screen: Screen;
  scenario: Scenario | null;
  // Whether the scenario in the editor is visible to instructors once saved.
  published: boolean;
  editorDraft: EditorDraft | null;
  // Scenarios imported from a file: kept for this browser session only, never uploaded.
  imported: Scenario[];
  openEditor: (scenario: Scenario, published?: boolean) => void;
  saveEditorDraft: (draft: EditorDraft) => void;
  openRun: () => void;
  openUsers: () => void;
  goStart: () => void;
  addImported: (scenario: Scenario) => void;
  removeImported: (id: string) => void;
};

export const useAppStore = create<AppStore>((set) => ({
  screen: 'start',
  scenario: null,
  published: false,
  editorDraft: null,
  imported: [],
  openEditor: (scenario, published = false) =>
    set({ screen: 'editor', scenario, published, editorDraft: null }),
  saveEditorDraft: (editorDraft) => set({ editorDraft }),
  openRun: () => set({ screen: 'run' }),
  openUsers: () => set({ screen: 'users' }),
  goStart: () => set({ screen: 'start', scenario: null, editorDraft: null }),
  addImported: (scenario) =>
    set((s) => ({ imported: [...s.imported.filter((i) => i.id !== scenario.id), scenario] })),
  removeImported: (id) => set((s) => ({ imported: s.imported.filter((i) => i.id !== id) })),
}));

// The saved draft, if it belongs to the scenario the editor is opening.
export function draftFor(
  state: Pick<AppStore, 'editorDraft'>,
  scenario: Scenario | null,
): EditorDraft | null {
  const draft = state.editorDraft;
  return draft && scenario && draft.scenario.id === scenario.id ? draft : null;
}
